import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Badge, Spinner } from 'reactstrap';
import { ArrowLeft, FileText, Search, Plus } from 'react-feather';
import { toast } from 'react-toastify';
import { Btn, H4, H5, H6, P } from '../../../AbstractElements';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import CaseFollowUpService from '../../../Api/services/case-follow-up.service';
import CaseHistoryTimeline from './CaseHistoryTimeline';
import QueryUSCISModal from './QueryUSCISModal';
import AddNoteModal from './AddNoteModal';
import type { components } from '../../../Api/types/openapi';

type CaseFollowUpUSCISResponse = components['schemas']['app__core__serializer__case_follow_up_lawyer__CaseFollowUpUSCISResponse'];
type CaseStatusHistoryUSCISResponse = components['schemas']['CaseStatusHistoryUSCISResponse'];

const CaseFollowUpDetail = () => {
  const { caseNumber } = useParams<{ caseNumber: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { t } = useTranslation();
  const [caseData, setCaseData] = useState<CaseFollowUpUSCISResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [queryModalOpen, setQueryModalOpen] = useState(false);
  const [addNoteModalOpen, setAddNoteModalOpen] = useState(false);

  useEffect(() => {
    if (caseNumber) {
      loadCase();
    }
  }, [caseNumber]);

  const loadCase = async () => {
    if (!caseNumber) return;
    
    try {
      setLoading(true);
      const data = await CaseFollowUpService.getLawyerCaseByNumber(caseNumber);
      setCaseData(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cargar caso');
      toast.error(errorMessage);
      navigate(`${process.env.PUBLIC_URL}/case-follow-up`);
    } finally {
      setLoading(false);
    }
  };

  const getCaseTypeBadge = (caseType: string | null | undefined) => {
    if (!caseType) return <Badge color="secondary">-</Badge>;
    
    switch (caseType) {
      case 'USCIS':
        return <Badge color="primary">USCIS</Badge>;
      case 'EOIR':
        return <Badge color="info">EOIR</Badge>;
      case 'CEAC':
        return <Badge color="success">CEAC</Badge>;
      default:
        return <Badge color="secondary">{caseType}</Badge>;
    }
  };

  const handleQuerySuccess = (result: CaseStatusHistoryUSCISResponse) => {
    loadCase();
  };

  const handleAddNoteSuccess = () => {
    loadCase();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner color="primary" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="text-center py-5">
        <p>{t('Caso no encontrado')}</p>
        <Btn color="primary" onClick={() => navigate(`${process.env.PUBLIC_URL}/case-follow-up`)}>
          {t('Volver a la lista')}
        </Btn>
      </div>
    );
  }

  const caseNumberValue = caseData.case_follow_up_base?.case_number_uscis || '-';
  const caseType = caseData.case_follow_up_base?.case_type;
  const histories = caseData.history_cases || [];

  return (
    <Container fluid>
      <Row>
        <Col md="12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center">
              <Btn
                color="secondary"
                className="me-2"
                onClick={() => navigate(`${process.env.PUBLIC_URL}/case-follow-up`)}
              >
                <ArrowLeft size={16} className="me-1" />
                {t('Volver')}
              </Btn>
              <H4 className="mb-0">{t('Información del Caso')}</H4>
            </div>
            <div>
              {(hasRole('admin') || hasRole('lawyer')) && (
                <>
                  <Btn
                    color="primary"
                    className="me-2"
                    onClick={() => setQueryModalOpen(true)}
                  >
                    <Search size={16} className="me-1" />
                    {t('Consultar USCIS')}
                  </Btn>
                  {histories.length > 0 && (
                    <Btn
                      color="info"
                      onClick={() => setAddNoteModalOpen(true)}
                    >
                      <Plus size={16} className="me-1" />
                      {t('Agregar Nota')}
                    </Btn>
                  )}
                </>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md="8">
          {/* Información General */}
          <Card className="mb-3">
            <CardHeader>
              <H5 className="mb-0">{t('Información del Caso')}</H5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="6">
                  <P className="mb-2">
                    <strong>{t('Alias/Nombre')}:</strong> {caseData.name || '-'}
                  </P>
                  <P className="mb-2">
                    <strong>{t('Número de Caso USCIS')}:</strong> {caseNumberValue}
                  </P>
                  <P className="mb-2">
                    <strong>{t('Tipo de Caso')}:</strong> {getCaseTypeBadge(caseType)}
                  </P>
                </Col>
                <Col md="6">
                  {caseData.case_follow_up_base?.alien_number_eoir && (
                    <P className="mb-2">
                      <strong>{t('Alien Number EOIR')}:</strong> {caseData.case_follow_up_base.alien_number_eoir}
                    </P>
                  )}
                  {caseData.created_at && (
                    <P className="mb-2">
                      <strong>{t('Fecha de Creación')}:</strong> {new Date(caseData.created_at).toLocaleDateString('es-ES')}
                    </P>
                  )}
                  {caseData.updated_at && (
                    <P className="mb-2">
                      <strong>{t('Última Actualización')}:</strong> {new Date(caseData.updated_at).toLocaleDateString('es-ES')}
                    </P>
                  )}
                  <P className="mb-2">
                    <strong>{t('Estado')}:</strong> {caseData.active ? <Badge color="success">{t('Activo')}</Badge> : <Badge color="secondary">{t('Inactivo')}</Badge>}
                  </P>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Historial */}
          <CaseHistoryTimeline histories={histories} />
        </Col>

        <Col md="4">
          {/* Relaciones */}
          {caseData.case_follow_up_base && (
            <Card>
              <CardHeader>
                <H5 className="mb-0">{t('Relaciones')}</H5>
              </CardHeader>
              <CardBody>
                <P className="text-muted">{t('Información de relaciones disponible en la vista completa')}</P>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>

      {/* Modales */}
      <QueryUSCISModal
        isOpen={queryModalOpen}
        toggle={() => setQueryModalOpen(false)}
        onSuccess={handleQuerySuccess}
      />

      <AddNoteModal
        isOpen={addNoteModalOpen}
        toggle={() => setAddNoteModalOpen(false)}
        caseHistories={histories}
        onSuccess={handleAddNoteSuccess}
      />
    </Container>
  );
};

export default CaseFollowUpDetail;

